import React, {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import { cartApi } from "../api/services";

import { useAuth } from "./AuthContext";

import {

  addGuestCartItem,

  clearRawGuestItems,

  getRawGuestItemsForMerge,

  hydrateGuestCart,

  removeGuestCartItem,

  updateGuestCartItem,

} from "../utils/guestCart";



const emptyCart = {

  items: [],

  subtotal: 0,

  vatRate: 0.05,

  vatAmount: 0,

  total: 0,

  currency: "AED",

};



const CartContext = createContext(null);



export function CartProvider({ children }) {

  const { isAuthenticated, loading: authLoading } = useAuth();

  const [cart, setCart] = useState(emptyCart);

  const [loading, setLoading] = useState(true);

  const wasGuestRef = useRef(true);



  const syncCart = useCallback((next) => {

    setCart(next || emptyCart);

  }, []);



  const refreshCart = useCallback(async () => {

    if (!isAuthenticated) {

      const guestCart = await hydrateGuestCart();

      setCart(guestCart);

      return guestCart;

    }

    try {

      const res = await cartApi.get();

      const next = res.data.data.cart || emptyCart;

      setCart(next);

      return next;

    } catch {

      setCart(emptyCart);

      return emptyCart;

    }

  }, [isAuthenticated]);



  const mergeGuestCartIntoServer = useCallback(async () => {

    const raw = getRawGuestItemsForMerge();

    if (!raw.length) return;



    for (const item of raw) {

      try {

        await cartApi.add(item.productId, item.quantity, {

          variantId: item.variantId,

          selectedAttributes: item.selectedAttributes || {},

        });

      } catch {

        // keep merging remaining items even if one fails

      }

    }

    clearRawGuestItems();

  }, []);



  useEffect(() => {

    if (authLoading) return;



    let cancelled = false;



    const load = async () => {

      setLoading(true);

      try {

        if (isAuthenticated && wasGuestRef.current) {

          wasGuestRef.current = false;

          await mergeGuestCartIntoServer();

        }

        if (!isAuthenticated) {

          wasGuestRef.current = true;

        }

        if (!cancelled) await refreshCart();

      } finally {

        if (!cancelled) setLoading(false);

      }

    };



    load();

    return () => {

      cancelled = true;

    };

  }, [authLoading, isAuthenticated, mergeGuestCartIntoServer, refreshCart]);



  const addToCart = useCallback(

    async (productId, quantity = 1, extras = {}) => {

      if (!isAuthenticated) {

        const next = await addGuestCartItem(productId, quantity, extras);

        setCart(next);

        return next;

      }

      const res = await cartApi.add(productId, quantity, extras);

      const next = res.data.data.cart || emptyCart;

      setCart(next);

      return next;

    },

    [isAuthenticated]

  );



  const updateQuantity = useCallback(

    async (itemId, quantity) => {

      if (quantity < 1) return;

      if (!isAuthenticated) {

        const next = await updateGuestCartItem(itemId, quantity);

        setCart(next);

        return next;

      }

      const res = await cartApi.update(itemId, quantity);

      const next = res.data.data.cart || emptyCart;

      setCart(next);

      return next;

    },

    [isAuthenticated]

  );



  const removeItem = useCallback(

    async (itemId) => {

      if (!isAuthenticated) {

        const next = await removeGuestCartItem(itemId);

        setCart(next);

        return next;

      }

      const res = await cartApi.remove(itemId);

      const next = res.data.data.cart || emptyCart;

      setCart(next);

      return next;

    },

    [isAuthenticated]

  );



  const clearCart = useCallback(() => {

    setCart(emptyCart);

    if (!isAuthenticated) clearRawGuestItems();

  }, [isAuthenticated]);



  const isInCart = useCallback(

    (productId, variantId = null) =>

      (cart.items || []).some((item) => {

        if (String(item.productId) !== String(productId)) return false;

        if (variantId != null) {

          return String(item.variantId || "") === String(variantId);

        }

        return true;

      }),

    [cart.items]

  );



  const cartCount = useMemo(() => (cart.items || []).length, [cart.items]);



  const value = useMemo(

    () => ({

      cart,

      cartCount,

      loading,

      refreshCart,

      syncCart,

      addToCart,

      updateQuantity,

      removeItem,

      clearCart,

      isInCart,

    }),

    [

      cart,

      cartCount,

      loading,

      refreshCart,

      syncCart,

      addToCart,

      updateQuantity,

      removeItem,

      clearCart,

      isInCart,

    ]

  );



  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;

}



export function useCart() {

  const ctx = useContext(CartContext);

  if (!ctx) throw new Error("useCart must be used within CartProvider");

  return ctx;

}

